import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearComorbidity,
    clearEmploymentStatus,
    clearGp,
    clearReligion,
    loadComorbidityDetails,
    loadEmploymentStatusDetails,
    loadGpDetails,
    loadReligionDetails
} from '../../../store/directusService';
import {
    updateDirectusContractSelections,
    updatePreSelects
} from '../../../utils/directusFunctions';
import {
    NO_CONTRACT_ERROR,
    NO_UPDATE_ERROR
} from '../../../utils/formValidation/commonErrorConstants';
import {
    COMORBIDITY_ERROR,
    EMPLOYMENT_STATUS_DETAILS_ERROR,
    GP_ERROR,
    RELIGION_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import '../../../commonStyles/loading.css';
import form from '../../../commonStyles/formStyles.module.css';

const comorbidityName = 'comorbidity';
const religionName = 'religion';
const gpName = 'gp';
const employmentStatusName = 'employment_status';

const submitMessages = {
    gps: 'GPs have been updated',
    comorbidities: 'Comorbidities have been updated',
    religion: 'Religion details have been updated',
    employmentStatuses: 'Employment status details have been updated'
};

const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];

const HealthInformationAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        selectedGps: [],
        preSelectedGpIds: [],
        preSelectedGps: [],

        selectedComorbidities: [],
        preSelectedComorbidityIds: [],
        preSelectedComorbidities: [],

        selectedReligion: [],
        preSelectedReligionIds: [],
        preSelectedReligion: [],

        selectedEmploymentStatuses: [],
        preSelectedEmploymentStatusIds: [],
        preSelectedEmploymentStatuses: []
    };

    const [admin, setAdmin] = useState(initialState);
    const [keys, setKeys] = useState({
        gps: '0',
        comorbidities: '1',
        religion: '2',
        employmentStatuses: '3'
    });
    const [showGPLoading, setShowGPLoading] = useState(false);
    const [showGPPending, setShowGPPending] = useState(false);
    const [arrayGps, setArrayGps] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const employmentStatusDetails = useSelector(
        (state) => state.entities.directusService.employmentStatusDetails
    );
    const gpDetails = useSelector((state) => state.entities.directusService.gpDetails);
    const gpDetailsLoading = useSelector(
        (state) => state.entities.directusService.gpDetailsLoading
    );
    const gpUpdatePending = useSelector((state) => state.entities.directusService.gpUpdatePending);
    const comorbidityDetails = useSelector(
        (state) => state.entities.directusService.comorbidityDetails
    );
    const religionDetails = useSelector((state) => state.entities.directusService.religionDetails);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) {
            setAdmin(initialState);
            setKeys(clearKeys(keys));
            setArrayGps([]);
            dispatch(clearGp());
            dispatch(clearComorbidity());
            dispatch(clearReligion());
            dispatch(clearEmploymentStatus());
            dispatch(loadGpDetails());
            dispatch(loadGpDetails(2));
            dispatch(loadGpDetails(3));
            dispatch(loadComorbidityDetails());
            dispatch(loadReligionDetails());
            dispatch(loadEmploymentStatusDetails());
        }
    }, [contractId]);

    useEffect(() => {
        if (gpDetails?.length > 2000 && arrayGps?.length === 0) {
            const { preSelects, preSelectIds } = updatePreSelects(gpName, gpDetails, contractId);
            setAdmin((admin) => ({
                ...admin,
                preSelectedGps: preSelects,
                preSelectedGpIds: preSelectIds
            }));
            setArrayGps(gpDetails);
        }
    }, [gpDetails]);

    useEffect(() => {
        if (comorbidityDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                comorbidityName,
                comorbidityDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedComorbidities: preSelects,
                preSelectedComorbidityIds: preSelectIds
            }));
        }
    }, [comorbidityDetails]);

    useEffect(() => {
        if (religionDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                religionName,
                religionDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedReligion: preSelects,
                preSelectedReligionIds: preSelectIds
            }));
        }
    }, [religionDetails]);

    useEffect(() => {
        if (employmentStatusDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                employmentStatusName,
                employmentStatusDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedEmploymentStatuses: preSelects,
                preSelectedEmploymentStatusIds: preSelectIds
            }));
        }
    }, [employmentStatusDetails]);

    useEffect(() => {
        setShowGPLoading(gpDetailsLoading);
    }, [gpDetailsLoading]);

    useEffect(() => {
        setShowGPPending(gpUpdatePending);
    }, [gpUpdatePending]);

    useEffect(() => {
        if (successMessage.includes(submitMessages.gps)) {
            setAdmin((admin) => ({
                ...admin,
                selectedGps: [],
                preSelectedGps: [],
                preSelectedGpIds: []
            }));
            setArrayGps([]);
            dispatch(clearGp());
            dispatch(loadGpDetails());
            dispatch(loadGpDetails(2));
            dispatch(loadGpDetails(3));
        }
        if (successMessage.includes(submitMessages.comorbidities)) {
            setAdmin((admin) => ({
                ...admin,
                selectedComorbidities: [],
                preSelectedComorbidityIds: [],
                preSelectedComorbidities: []
            }));
            dispatch(clearComorbidity());
            dispatch(loadComorbidityDetails());
        }
        if (successMessage.includes(submitMessages.religion)) {
            setAdmin((admin) => ({
                ...admin,
                selectedReligion: [],
                preSelectedReligionIds: [],
                preSelectedReligion: []
            }));
            dispatch(clearReligion());
            dispatch(loadReligionDetails());
        }
        if (successMessage.includes(submitMessages.employmentStatuses)) {
            setAdmin((admin) => ({
                ...admin,
                selectedEmploymentStatuses: [],
                preSelectedEmploymentStatusIds: [],
                preSelectedEmploymentStatuses: []
            }));
            dispatch(clearEmploymentStatus());
            dispatch(loadEmploymentStatusDetails());
        }
        isButtonDisabled && setIsButtonDisabled(false);
    }, [successMessage, errorMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (gpDetails?.length < 1) return GP_ERROR;
        if (comorbidityDetails?.length < 1) return COMORBIDITY_ERROR;
        if (religionDetails?.length < 1) return RELIGION_ERROR;
        if (employmentStatusDetails?.length < 1) return EMPLOYMENT_STATUS_DETAILS_ERROR;
    };

    const clearButtonError = () => {
        setErrors({});
        setIsButtonDisabled(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setAdmin((admin) => ({ ...admin, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        let isUpdated = false;

        const isUpdatedGps = updateDirectusContractSelections(
            gpName,
            admin.preSelectedGpIds,
            admin.preSelectedGps,
            admin.selectedGps,
            contractId,
            submitMessages.gps,
            dispatch
        );

        const isUpdatedComorbidities = updateDirectusContractSelections(
            comorbidityName,
            admin.preSelectedComorbidityIds,
            admin.preSelectedComorbidities,
            admin.selectedComorbidities,
            contractId,
            submitMessages.comorbidities,
            dispatch
        );

        const isUpdatedReligion = updateDirectusContractSelections(
            religionName,
            admin.preSelectedReligionIds,
            admin.preSelectedReligion,
            admin.selectedReligion,
            contractId,
            submitMessages.religion,
            dispatch
        );

        const isUpdatedEmploymentStatuses = updateDirectusContractSelections(
            employmentStatusName,
            admin.preSelectedEmploymentStatusIds,
            admin.preSelectedEmploymentStatuses,
            admin.selectedEmploymentStatuses,
            contractId,
            submitMessages.employmentStatuses,
            dispatch
        );

        if (
            isUpdatedGps ||
            isUpdatedComorbidities ||
            isUpdatedReligion ||
            isUpdatedEmploymentStatuses
        ) {
            isUpdated = true;
        }

        !isUpdated &&
            setErrors((prev) => ({ ...prev, button: { error: true, message: NO_UPDATE_ERROR } }));
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            onSubmit={onSubmit}
            className={form.formWrapper}
            data-testid="form_start_healthInformationAdmin">
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <MultiSelect
                        id="gps"
                        key={keys.gps}
                        label="GP"
                        placeholder="Select GPs..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={gpDetails || []}
                        preSelectedIds={admin.preSelectedGpIds}
                        preSelects={admin.preSelectedGps}
                        onChange={(chosenIds) => onUpdate('selectedGps', chosenIds)}
                    />
                    {showGPLoading && <div className="ddlLoadingIcon">Loading...</div>}
                    {showGPPending && <div className="ddlLoadingIcon">Pending...</div>}

                    <MultiSelect
                        id="comorbidities"
                        key={keys.comorbidities}
                        label="Comorbidity"
                        placeholder="Select comorbidities..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={comorbidityDetails || []}
                        preSelectedIds={admin.preSelectedComorbidityIds}
                        preSelects={admin.preSelectedComorbidities}
                        onChange={(chosenIds) => onUpdate('selectedComorbidities', chosenIds)}
                    />
                </div>
                <div className={form.formColumn}>
                    <MultiSelect
                        id="religion"
                        key={keys.religion}
                        label="Religion"
                        placeholder="Select religion details..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={religionDetails || []}
                        preSelectedIds={admin.preSelectedReligionIds}
                        preSelects={admin.preSelectedReligion}
                        onChange={(chosenIds) => onUpdate('selectedReligion', chosenIds)}
                    />
                    <MultiSelect
                        id="employmentStatus"
                        key={keys.employmentStatuses}
                        label="Employment Status"
                        placeholder="Select employment status details..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={employmentStatusDetails || []}
                        preSelectedIds={admin.preSelectedEmploymentStatusIds}
                        preSelects={admin.preSelectedEmploymentStatuses}
                        onChange={(chosenIds) => onUpdate('selectedEmploymentStatuses', chosenIds)}
                    />
                </div>
            </div>
            <div>
                <p>Doctor&apos;s Statement</p>
                <p>Pregnant?</p>
                <p>Midwife&apos;s Name</p>
                <p>Midwife&apos;s Phone</p>
                <p>Midwife&apos;s Email</p>
                <p>Smoker?</p>
                <p>Employed?</p>
            </div>

            <Button
                id="healthInformationAdminButton"
                content="UPDATE HEALTH INFORMATION"
                disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                error={errors.button}
                clearError={clearButtonError}
            />
        </form>
    );
};

HealthInformationAdmin.propTypes = {
    contractId: PropTypes.string
};

export default HealthInformationAdmin;
